import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, Users, Building, Gavel, Shield, Zap, Target, Eye,
  CheckCircle, ArrowRight, ChevronDown, Play, BarChart3, Globe,
  Clock, Award, Sparkles, Rocket, PieChart, LineChart, DollarSign,
  Home, UserCheck, Briefcase, Lock, Brain, Smartphone, Mail
} from 'lucide-react';

const InvestorPitchPage = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [countersVisible, setCountersVisible] = useState(false);
  const countersRef = useRef(null);

  // Animated counter hook
  const useCounter = (end, duration = 2000, start = 0) => {
    const [count, setCount] = useState(start);
    
    useEffect(() => {
      if (!countersVisible) return;
      
      let startTime = null;
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        setCount(Math.floor(progress * (end - start) + start));
        if (progress < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }, [countersVisible, end, duration, start]);
    
    return count;
  };

  // Intersection observer for counters
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setCountersVisible(true);
      },
      { threshold: 0.3 }
    );
    
    if (countersRef.current) observer.observe(countersRef.current);
    return () => observer.disconnect();
  }, []);

  const marketSize = useCounter(145, 2500);
  const transactions = useCounter(720, 2500);
  const avgTime = useCounter(180, 2000);

  const players = [
    {
      icon: Home,
      title: 'Proprietari',
      subtitle: 'Vendi al massimo valore',
      color: 'from-blue-500 to-cyan-500',
      benefits: [
        { icon: TrendingUp, text: 'Prezzo finale più alto grazie alla competizione tra acquirenti' },
        { icon: Clock, text: 'Tempi di vendita ridotti del 60% rispetto al metodo tradizionale' },
        { icon: Shield, text: 'Zero rischi: nessun costo se non vendi' },
        { icon: Eye, text: 'Trasparenza totale su offerte e processo' },
        { icon: UserCheck, text: 'Agenti verificati e qualificati' }
      ],
      stats: { value: '+23%', label: 'Prezzo medio vs mercato' }
    },
    {
      icon: Users,
      title: 'Acquirenti',
      subtitle: 'Compra in modo trasparente',
      color: 'from-emerald-500 to-teal-500',
      benefits: [
        { icon: Eye, text: 'Visibilità completa su tutte le offerte in tempo reale' },
        { icon: Shield, text: 'Nessun gioco al ribasso o trattative nascoste' },
        { icon: Target, text: 'Prezzo di mercato reale, determinato dalla domanda' },
        { icon: Lock, text: 'Depositi sicuri e protetti' },
        { icon: Zap, text: 'Processo digitale veloce e senza burocrazia' }
      ],
      stats: { value: '100%', label: 'Trasparenza garantita' }
    },
    {
      icon: Briefcase,
      title: 'Agenti',
      subtitle: 'Lavora in modo efficiente',
      color: 'from-violet-500 to-purple-500',
      benefits: [
        { icon: Zap, text: 'Gestione digitale completa del portfolio immobili' },
        { icon: TrendingUp, text: 'Commissioni più alte grazie ai prezzi finali superiori' },
        { icon: Clock, text: 'Meno tempo per singola transazione, più deal chiusi' },
        { icon: Brain, text: 'Strumenti AI per valutazioni e documentazione' },
        { icon: Award, text: 'Reputazione verificata e recensioni pubbliche' }
      ],
      stats: { value: '3x', label: 'Produttività media' }
    }
  ];

  const problemStats = [
    { value: '180', unit: 'giorni', label: 'Tempo medio vendita Italia' },
    { value: '15%', unit: '', label: 'Immobili venduti sotto prezzo' },
    { value: '67%', unit: '', label: 'Venditori insoddisfatti del processo' },
    { value: '€2.4B', unit: '', label: 'Valore perso annualmente' }
  ];

  const businessModel = [
    { source: 'Commissione Vendita', percentage: 60, description: '2-3% sul prezzo finale' },
    { source: 'Abbonamenti Agenti', percentage: 25, description: 'Piani Pro e Enterprise' },
    { source: 'Servizi Premium', percentage: 15, description: 'Virtual staging, video AI, boost' }
  ];

  const roadmap = [
    { phase: 'Q1 2025', title: 'Launch Italia', items: ['Roma', 'Milano', 'Napoli'], status: 'completed' },
    { phase: 'Q2 2025', title: 'Espansione', items: ['10 città principali', 'App Mobile'], status: 'current' },
    { phase: 'Q4 2025', title: 'Scale', items: ['Copertura nazionale', '1000 agenti'], status: 'upcoming' },
    { phase: '2026', title: 'Europa', items: ['Spagna', 'Francia', 'Germania'], status: 'upcoming' }
  ];

  return (
    <div className="min-h-screen bg-[#000000] text-white overflow-x-hidden">
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <img src="/weqasa-logo.png" alt="WEQASA" className="h-8 w-auto brightness-0 invert" />
          </Link>
          <div className="flex items-center gap-6">
            <span className="text-sm text-white">Investor Deck</span>
            <a href="mailto:investors@weqasa.it" className="px-4 py-2 bg-[#0071e3] rounded-full text-sm font-medium hover:bg-[#0077ED] transition">
              Contattaci
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#0071e3]/20 rounded-full blur-[120px] animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-[120px] animate-pulse delay-1000" />
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur rounded-full mb-8">
            <Sparkles className="w-4 h-4 text-[#0071e3]" />
            <span className="text-sm font-medium">Opportunità di Investimento</span>
          </div>
          
          <h1 className="text-5xl sm:text-6xl lg:text-8xl font-bold tracking-tight mb-6 text-white">
            Il futuro del
            <span className="block bg-gradient-to-r from-[#0071e3] to-cyan-400 bg-clip-text text-transparent">
              Real Estate
            </span>
            <span className="block text-white">è qui.</span>
          </h1>
          
          <p className="text-xl sm:text-2xl text-white max-w-3xl mx-auto mb-12 leading-relaxed">
            WEQASA rivoluziona la compravendita immobiliare in Italia con un sistema 
            di <strong className="text-white">Gara di Vendita digitale</strong> che crea valore 
            per tutti: venditori, acquirenti e agenti.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#opportunity" className="inline-flex items-center justify-center px-8 py-4 bg-[#0071e3] rounded-full text-lg font-semibold hover:bg-[#0077ED] transition-all hover:scale-105">
              Scopri l'opportunità
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
            <Link to="/demo" className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur rounded-full text-lg font-semibold hover:bg-white/20 transition">
              <Play className="mr-2 h-5 w-5" />
              Guarda la Demo
            </Link>
          </div>
          
          <div className="mt-20 animate-bounce">
            <ChevronDown className="w-8 h-8 mx-auto text-white" />
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section id="problem" className="py-32 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <span className="text-[#0071e3] font-semibold text-sm uppercase tracking-wider">Il Problema</span>
            <h2 className="text-4xl sm:text-5xl font-bold mt-4 mb-6 text-white">
              Il mercato immobiliare italiano<br />è fermo al secolo scorso
            </h2>
            <p className="text-xl text-white max-w-3xl mx-auto">
              Processi lenti, mancanza di trasparenza e inefficienze che costano miliardi ogni anno.
            </p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" ref={countersRef}>
            {problemStats.map((stat, index) => (
              <div key={index} className="bg-white/5 backdrop-blur rounded-2xl p-8 text-center border border-white/10 hover:border-red-500/50 transition-colors">
                <div className="text-4xl sm:text-5xl font-bold text-red-400 mb-2">
                  {stat.value}
                  <span className="text-2xl">{stat.unit}</span>
                </div>
                <p className="text-white">{stat.label}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-16 grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-red-500/10 to-transparent rounded-2xl p-8 border border-red-500/20">
              <h3 className="text-xl font-semibold mb-4 text-red-400">Per i Venditori</h3>
              <ul className="space-y-3 text-white">
                <li>• Tempi di vendita imprevedibili</li>
                <li>• Nessuna certezza sul prezzo finale</li>
                <li>• Trattative estenuanti e opache</li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-orange-500/10 to-transparent rounded-2xl p-8 border border-orange-500/20">
              <h3 className="text-xl font-semibold mb-4 text-orange-400">Per gli Acquirenti</h3>
              <ul className="space-y-3 text-white">
                <li>• Zero visibilità su altre offerte</li>
                <li>• Paura di pagare troppo</li>
                <li>• Processo frustrante e lungo</li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-yellow-500/10 to-transparent rounded-2xl p-8 border border-yellow-500/20">
              <h3 className="text-xl font-semibold mb-4 text-yellow-400">Per gli Agenti</h3>
              <ul className="space-y-3 text-white">
                <li>• Troppo tempo per singola vendita</li>
                <li>• Gestione manuale inefficiente</li>
                <li>• Difficoltà a scalare il business</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section id="solution" className="py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <span className="text-[#0071e3] font-semibold text-sm uppercase tracking-wider">La Soluzione</span>
            <h2 className="text-4xl sm:text-5xl font-bold mt-4 mb-6 text-white">
              La Gara di Vendita Digitale
            </h2>
            <p className="text-xl text-white max-w-3xl mx-auto">
              Un sistema innovativo che trasforma ogni vendita in un'opportunità competitiva,
              dove tutti vincono.
            </p>
          </div>
          
          <div className="relative">
            {/* Process Steps */}
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { step: '01', title: 'Inserimento', desc: 'Il proprietario inserisce l\'immobile. Un agente verificato lo prende in carico.', icon: Building },
                { step: '02', title: 'Preparazione', desc: 'Foto professionali, virtual tour, documentazione completa e valutazione AI.', icon: Eye },
                { step: '03', title: 'Gara', desc: 'Acquirenti qualificati competono con offerte al rialzo in tempo reale.', icon: Gavel },
                { step: '04', title: 'Vendita', desc: 'Il miglior offerente vince. Contratto e rogito gestiti digitalmente.', icon: CheckCircle }
              ].map((item, index) => (
                <div key={index} className="relative">
                  <div className="bg-gradient-to-br from-[#0071e3]/20 to-transparent rounded-2xl p-8 border border-[#0071e3]/30 h-full">
                    <div className="text-6xl font-bold text-[#0071e3]/30 mb-4">{item.step}</div>
                    <item.icon className="w-10 h-10 text-[#0071e3] mb-4" />
                    <h3 className="text-xl font-semibold mb-3 text-white">{item.title}</h3>
                    <p className="text-white">{item.desc}</p>
                  </div>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                      <ArrowRight className="w-8 h-8 text-[#0071e3]/50" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Players Benefits Section - KEY FOCUS */}
      <section id="opportunity" className="py-32 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <span className="text-[#0071e3] font-semibold text-sm uppercase tracking-wider">Vantaggi Innovativi</span>
            <h2 className="text-4xl sm:text-5xl font-bold mt-4 mb-6 text-white">
              Valore per tutti i player
            </h2>
            <p className="text-xl text-white max-w-3xl mx-auto">
              WEQASA crea un ecosistema win-win-win dove ogni partecipante ottiene più valore rispetto al mercato tradizionale.
            </p>
          </div>
          
          <div className="space-y-12">
            {players.map((player, index) => (
              <div 
                key={index}
                className={`relative rounded-3xl overflow-hidden ${
                  index % 2 === 0 ? '' : 'md:flex-row-reverse'
                }`}
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${player.color} opacity-10`} />
                <div className="relative grid md:grid-cols-2 gap-8 p-8 md:p-12">
                  {/* Left - Info */}
                  <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${player.color} mb-6`}>
                      <player.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-3xl font-bold mb-2 text-white">{player.title}</h3>
                    <p className="text-xl text-white mb-8">{player.subtitle}</p>
                    
                    <div className="space-y-4">
                      {player.benefits.map((benefit, bIndex) => (
                        <div key={bIndex} className="flex items-start gap-4">
                          <div className={`flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${player.color} bg-opacity-20 flex items-center justify-center`}>
                            <benefit.icon className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-white pt-2">{benefit.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Right - Stats Card */}
                  <div className={`flex items-center justify-center ${index % 2 === 1 ? 'md:order-1' : ''}`}>
                    <div className={`bg-gradient-to-br ${player.color} rounded-3xl p-12 text-center transform hover:scale-105 transition-transform`}>
                      <div className="text-7xl font-bold mb-4 text-white">{player.stats.value}</div>
                      <p className="text-xl text-white">{player.stats.label}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Market Opportunity */}
      <section id="market" className="py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <span className="text-[#0071e3] font-semibold text-sm uppercase tracking-wider">Opportunità di Mercato</span>
            <h2 className="text-4xl sm:text-5xl font-bold mt-4 mb-6 text-white">
              Un mercato da €145 miliardi
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-gradient-to-br from-[#0071e3]/20 to-[#0071e3]/5 rounded-3xl p-10 text-center border border-[#0071e3]/30">
              <Globe className="w-12 h-12 text-[#0071e3] mx-auto mb-6" />
              <div className="text-5xl font-bold mb-2 text-white">€145B</div>
              <p className="text-white text-lg">TAM - Mercato Immobiliare Italia</p>
            </div>
            <div className="bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 rounded-3xl p-10 text-center border border-cyan-500/30">
              <Target className="w-12 h-12 text-cyan-400 mx-auto mb-6" />
              <div className="text-5xl font-bold mb-2 text-white">€28B</div>
              <p className="text-white text-lg">SAM - Segmento Premium/Urbano</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 rounded-3xl p-10 text-center border border-emerald-500/30">
              <Rocket className="w-12 h-12 text-emerald-400 mx-auto mb-6" />
              <div className="text-5xl font-bold mb-2 text-white">€2.8B</div>
              <p className="text-white text-lg">SOM - Target 5 anni</p>
            </div>
          </div>
          
          <div className="bg-white/5 backdrop-blur rounded-3xl p-10 border border-white/10">
            <h3 className="text-2xl font-bold mb-8 text-center text-white">Perché ora?</h3>
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { icon: Smartphone, title: 'Digitalizzazione', desc: 'Post-COVID adoption digitale accelerata nel RE' },
                { icon: Users, title: 'Generazione Y/Z', desc: 'Nuovi acquirenti nativi digitali' },
                { icon: Shield, title: 'Regolamentazione', desc: 'Spinta verso trasparenza e tracciabilità' },
                { icon: Brain, title: 'AI Revolution', desc: 'Tecnologie mature per automazione' }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <item.icon className="w-10 h-10 text-[#0071e3] mx-auto mb-4" />
                  <h4 className="font-semibold mb-2 text-white">{item.title}</h4>
                  <p className="text-white text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Business Model */}
      <section id="business" className="py-32 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <span className="text-[#0071e3] font-semibold text-sm uppercase tracking-wider">Business Model</span>
            <h2 className="text-4xl sm:text-5xl font-bold mt-4 mb-6 text-white">
              Revenue diversificato e scalabile
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              {businessModel.map((item, index) => (
                <div key={index} className="bg-white/5 rounded-2xl p-6 border border-white/10">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-xl font-semibold text-white">{item.source}</h3>
                    <span className="text-2xl font-bold text-[#0071e3]">{item.percentage}%</span>
                  </div>
                  <p className="text-white mb-4">{item.description}</p>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#0071e3] to-cyan-400 rounded-full transition-all duration-1000"
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            
            <div className="bg-gradient-to-br from-[#0071e3]/20 to-purple-500/20 rounded-3xl p-10 border border-[#0071e3]/30">
              <h3 className="text-2xl font-bold mb-8 text-white">Unit Economics</h3>
              <div className="space-y-6">
                <div className="flex justify-between items-center py-4 border-b border-white/10">
                  <span className="text-white">Valore medio transazione</span>
                  <span className="text-2xl font-bold text-white">€320K</span>
                </div>
                <div className="flex justify-between items-center py-4 border-b border-white/10">
                  <span className="text-white">Commissione media</span>
                  <span className="text-2xl font-bold text-white">2.5%</span>
                </div>
                <div className="flex justify-between items-center py-4 border-b border-white/10">
                  <span className="text-white">Revenue per transazione</span>
                  <span className="text-2xl font-bold text-[#0071e3]">€8,000</span>
                </div>
                <div className="flex justify-between items-center py-4">
                  <span className="text-white">Gross Margin</span>
                  <span className="text-2xl font-bold text-emerald-400">75%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology */}
      <section id="tech" className="py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <span className="text-[#0071e3] font-semibold text-sm uppercase tracking-wider">Tecnologia</span>
            <h2 className="text-4xl sm:text-5xl font-bold mt-4 mb-6 text-white">
              AI-Powered Real Estate Platform
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                icon: Brain, 
                title: 'Valutazione AI', 
                desc: 'Algoritmi proprietari per valutazioni immobiliari accurate al 95%',
                tech: ['Machine Learning', 'Computer Vision', 'Market Data']
              },
              { 
                icon: Shield, 
                title: 'Sicurezza Enterprise', 
                desc: 'Crittografia end-to-end, 2FA, conformità GDPR e antiriciclaggio',
                tech: ['AES-256', 'OAuth 2.0', 'Audit Trail']
              },
              { 
                icon: Zap, 
                title: 'Real-time Engine', 
                desc: 'Sistema di aste in tempo reale con latenza <100ms',
                tech: ['WebSocket', 'Redis', 'MongoDB']
              }
            ].map((item, index) => (
              <div key={index} className="bg-white/5 rounded-3xl p-8 border border-white/10 hover:border-[#0071e3]/50 transition-colors">
                <item.icon className="w-12 h-12 text-[#0071e3] mb-6" />
                <h3 className="text-xl font-bold mb-3 text-white">{item.title}</h3>
                <p className="text-white mb-6">{item.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {item.tech.map((t, tIndex) => (
                    <span key={tIndex} className="px-3 py-1 bg-[#0071e3]/20 rounded-full text-xs text-[#0071e3]">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section id="roadmap" className="py-32 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <span className="text-[#0071e3] font-semibold text-sm uppercase tracking-wider">Roadmap</span>
            <h2 className="text-4xl sm:text-5xl font-bold mt-4 mb-6 text-white">
              Il nostro percorso di crescita
            </h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {roadmap.map((phase, index) => (
              <div 
                key={index}
                className={`relative rounded-2xl p-8 border ${
                  phase.status === 'completed' ? 'bg-emerald-500/10 border-emerald-500/30' :
                  phase.status === 'current' ? 'bg-[#0071e3]/10 border-[#0071e3]/50 ring-2 ring-[#0071e3]/50' :
                  'bg-white/5 border-white/10'
                }`}
              >
                {phase.status === 'current' && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 bg-[#0071e3] rounded-full text-xs font-bold">
                    IN CORSO
                  </div>
                )}
                <div className="text-sm text-white mb-2">{phase.phase}</div>
                <h3 className="text-xl font-bold mb-4 text-white">{phase.title}</h3>
                <ul className="space-y-2">
                  {phase.items.map((item, iIndex) => (
                    <li key={iIndex} className="flex items-center gap-2 text-white">
                      <CheckCircle className={`w-4 h-4 ${phase.status === 'completed' ? 'text-emerald-400' : 'text-white'}`} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-32 bg-black relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#0071e3]/20 rounded-full blur-[150px]" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl sm:text-6xl font-bold mb-6 text-white">
            Unisciti alla rivoluzione del Real Estate
          </h2>
          <p className="text-xl text-white mb-12 max-w-2xl mx-auto">
            Stiamo cercando partner strategici e investitori che condividono la nostra visione 
            di un mercato immobiliare più trasparente, efficiente e digitale.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <a 
              href="mailto:investors@weqasa.it"
              className="inline-flex items-center justify-center px-10 py-5 bg-[#0071e3] rounded-full text-xl font-semibold hover:bg-[#0077ED] transition-all hover:scale-105"
            >
              <Mail className="mr-3 h-6 w-6" />
              Contatta il Team
            </a>
            <Link 
              to="/"
              className="inline-flex items-center justify-center px-10 py-5 bg-white/10 backdrop-blur rounded-full text-xl font-semibold hover:bg-white/20 transition"
            >
              Visita la Piattaforma
              <ArrowRight className="ml-3 h-6 w-6" />
            </Link>
          </div>
          
          <div className="grid grid-cols-3 gap-8 max-w-lg mx-auto">
            <div>
              <div className="text-3xl font-bold text-[#0071e3]">€1M</div>
              <p className="text-white text-sm">Seed Round</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#0071e3]">18</div>
              <p className="text-white text-sm">Mesi Runway</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#0071e3]">10x</div>
              <p className="text-white text-sm">Target ROI</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <img src="/weqasa-logo.png" alt="WEQASA" className="h-6 w-auto brightness-0 invert opacity-50" />
          <p className="text-white text-sm">
            © 2025 WEQASA S.r.l. • Documento riservato per potenziali investitori
          </p>
        </div>
      </footer>
    </div>
  );
};

export default InvestorPitchPage;
